import React, { useMemo } from 'react';
import { Download } from 'lucide-react';
import { useDashboard } from '@/hooks/useDashboard';
import { KpiCard } from '@/components/dashboard/KpiCard';
import { PrazosCriticosCard } from '@/components/dashboard/PrazosCriticosCard';
import { InsightsIACard } from '@/components/dashboard/InsightsIACard';
import { PipelineCard } from '@/components/dashboard/PipelineCard';
import { AtividadeRecenteCard } from '@/components/dashboard/AtividadeRecenteCard';
import { Button } from '@/components/ui/Button';

const BRAND_500  = '#CB6A1E';
const SUCCESS    = '#1E8A3C';
const INK_SUB    = '#74706A';

const Dashboard: React.FC = () => {
  const { data, isLoading, isEmpty, error, reload } = useDashboard();

  // Nome do usuário para saudação
  const primeiroNome = useMemo(() => {
    try {
      const userJson = localStorage.getItem('user');
      if (!userJson) return '';
      const u = JSON.parse(userJson);
      return (u?.name || '').split(' ')[0] || '';
    } catch { return ''; }
  }, []);

  // Estado de erro — banner no topo
  if (error) {
    return (
      <div className="mx-auto max-w-[1480px] px-6 py-6">
        <div className="p-4 rounded-lg bg-danger-soft dark:bg-danger-soft-dark border border-[rgba(196,50,28,0.25)] flex items-center justify-between gap-4">
          <div className="text-[13px] text-danger dark:text-danger-dark">
            Erro ao carregar o dashboard: {error.message}
          </div>
          <Button variant="secondary" onClick={reload}>Tentar novamente</Button>
        </div>
      </div>
    );
  }

  // Estado vazio — conta nova sem dados
  if (!isLoading && isEmpty) {
    return (
      <div className="mx-auto max-w-[1480px] px-6 py-20 text-center">
        <h2 className="text-[20px] font-semibold text-ink-title dark:text-ink-title-dark">
          Bem-vindo ao IaSolicitar
        </h2>
        <p className="mt-2 text-[14px] text-ink-subtitle dark:text-ink-subtitle-dark">
          Comece cadastrando seu primeiro edital para ver os indicadores aqui.
        </p>
        <div className="mt-6">
          <Button onClick={() => { window.location.hash = '/tenders'; }}>
            Buscar primeiro edital
          </Button>
        </div>
      </div>
    );
  }

  const { kpis, prazosCriticos, insights, pipeline, atividade, totalPrazos7d } = data;

  return (
    <div className="mx-auto max-w-[1480px] px-4 md:px-6 py-5 md:py-6 pb-12">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 mb-5">
        <div className="min-w-0">
          <h1 className="text-[22px] font-semibold tracking-[-0.01em] text-ink-title dark:text-ink-title-dark">
            {primeiroNome ? `Olá, ${primeiroNome}.` : 'Visão geral'}
          </h1>
          <p className="mt-1 text-[13.5px] text-ink-subtitle dark:text-ink-subtitle-dark">
            Você tem{' '}
            <strong className="text-ink-title dark:text-ink-title-dark tabular">{totalPrazos7d}</strong>{' '}
            prazo{totalPrazos7d === 1 ? '' : 's'} crítico{totalPrazos7d === 1 ? '' : 's'} e{' '}
            <strong className="text-ink-title dark:text-ink-title-dark tabular">{insights.length}</strong>{' '}
            recomendaç{insights.length === 1 ? 'ão' : 'ões'} da IA.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 flex-shrink-0">
          <select
            className="h-[34px] px-3 text-[13px] rounded-lg bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-title dark:text-ink-title-dark focus:border-brand-500 focus:shadow-focus dark:focus:shadow-focus-dark outline-none"
            defaultValue="90"
            aria-label="Intervalo"
          >
            <option value="30">Últimos 30 dias</option>
            <option value="90">Últimos 90 dias</option>
            <option value="365">Este ano</option>
            <option value="all">Tudo</option>
          </select>
          <Button variant="secondary" icon={<Download className="w-3.5 h-3.5" />}>
            Exportar
          </Button>
        </div>
      </div>

      {/* Seção 1 — KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3.5">
        <KpiCard
          label="Editais em acompanhamento"
          value={kpis.editaisAcompanhando.display}
          change={kpis.editaisAcompanhando.change}
          data={kpis.editaisAcompanhando.series}
          accent={BRAND_500}
          loading={isLoading}
        />
        <KpiCard
          label="Licitações ganhas (YTD)"
          value={kpis.licitacoesGanhasYtd.display}
          change={kpis.licitacoesGanhasYtd.change}
          data={kpis.licitacoesGanhasYtd.series}
          accent={SUCCESS}
          loading={isLoading}
        />
        <KpiCard
          label="Taxa de conversão"
          value={kpis.taxaConversao.display}
          change={kpis.taxaConversao.change}
          data={kpis.taxaConversao.series}
          accent={BRAND_500}
          loading={isLoading}
        />
        <KpiCard
          label="Contratos ativos"
          value={kpis.contratosAtivos.display}
          change={kpis.contratosAtivos.change}
          data={kpis.contratosAtivos.series}
          accent={INK_SUB}
          loading={isLoading}
        />
      </div>

      {/* Seção 2 — Prazos + Insights */}
      <div className="grid grid-cols-1 xl:grid-cols-[1.7fr_1fr] gap-3.5 mt-3.5">
        <PrazosCriticosCard items={prazosCriticos} loading={isLoading} />
        <InsightsIACard insights={insights} loading={isLoading} />
      </div>

      {/* Seção 3 — Pipeline + Atividade */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3.5 mt-3.5">
        <PipelineCard stages={pipeline} loading={isLoading} />
        <AtividadeRecenteCard items={atividade} loading={isLoading} />
      </div>
    </div>
  );
};

export default Dashboard;
